import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Eye, X, User, Building2, ChevronDown, ArrowRightLeft, Loader2 } from 'lucide-react';
import { useImpersonation } from '@/hooks/useImpersonation';
import { toast } from 'sonner';

interface ImpersonationInfo {
  sessionId: string;
  type: 'patient' | 'agency';
  targetName: string;
  targetId: string;
}

interface Agency {
  id: string;
  name: string;
  code: string;
}

interface Patient {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
}

export function ImpersonationBanner() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { endImpersonation, logImpersonationAction, startImpersonation } = useImpersonation();
  const [info, setInfo] = useState<ImpersonationInfo | null>(null);
  const [agencies, setAgencies] = useState<Agency[]>([]);
  const [patients, setPatients] = useState<Patient[]>([]);
  const [isLoadingTargets, setIsLoadingTargets] = useState(false);
  const [isSwitching, setIsSwitching] = useState(false);

  useEffect(() => {
    const impersonateType = searchParams.get('impersonate');
    const sessionId = searchParams.get('sid');
    
    if (!impersonateType || !sessionId) {
      setInfo(null);
      return;
    }

    // Load impersonation details
    const loadInfo = async () => {
      if (impersonateType === 'patient') {
        const patientId = searchParams.get('pid');
        if (patientId) {
          const { data: patient } = await supabase
            .from('patients')
            .select('first_name, last_name')
            .eq('id', patientId)
            .single();
          
          if (patient) {
            setInfo({
              sessionId,
              type: 'patient',
              targetName: `${patient.first_name} ${patient.last_name}`,
              targetId: patientId,
            });
          }
        }
      } else if (impersonateType === 'agency') {
        const agencyId = searchParams.get('aid');
        if (agencyId) {
          const { data: agency } = await supabase
            .from('agencies')
            .select('name')
            .eq('id', agencyId)
            .single();
          
          if (agency) {
            setInfo({
              sessionId,
              type: 'agency',
              targetName: agency.name,
              targetId: agencyId,
            });
          }
        }
      }
    };

    loadInfo();

    // Log page view
    logImpersonationAction(sessionId, 'page_view', { url: window.location.pathname });
  }, [searchParams, logImpersonationAction]);

  // Load all agencies for the switcher (when impersonating agency)
  useEffect(() => {
    const loadAgencies = async () => {
      if (info?.type !== 'agency') return;
      
      setIsLoadingTargets(true);
      try {
        const { data } = await supabase
          .from('agencies')
          .select('id, name, code')
          .eq('is_approved', true)
          .order('name');
        
        if (data) {
          setAgencies(data);
        }
      } catch (error) {
        console.error('Error loading agencies:', error);
      } finally {
        setIsLoadingTargets(false);
      }
    };

    loadAgencies();
  }, [info?.type]);

  // Load all patients for the switcher (when impersonating patient)
  useEffect(() => {
    const loadPatients = async () => {
      if (info?.type !== 'patient') return;
      
      setIsLoadingTargets(true);
      try {
        const { data } = await supabase
          .from('patients')
          .select('id, first_name, last_name, email')
          .eq('is_active', true)
          .order('last_name')
          .limit(50);
        
        if (data) {
          setPatients(data);
        }
      } catch (error) {
        console.error('Error loading patients:', error);
      } finally {
        setIsLoadingTargets(false);
      }
    };

    loadPatients();
  }, [info?.type]);

  const handleEndImpersonation = async () => {
    if (info?.sessionId) {
      await endImpersonation(info.sessionId);
    }
    // Navigate back to the main admin dashboard without impersonation params
    navigate('/admin/dashboard', { replace: true });
    toast.success('Impersonation session ended');
  };

  const handleSwitchAgency = async (agency: Agency) => {
    if (!info || agency.id === info.targetId || isSwitching) return;

    setIsSwitching(true);
    try {
      // End current session
      await endImpersonation(info.sessionId);

      // Start new impersonation session
      const newSessionId = await startImpersonation({
        type: 'agency_admin',
        agencyId: agency.id,
        agencyName: agency.name,
      });

      if (newSessionId) {
        // Navigate to the same page with new impersonation params
        const newUrl = `${location.pathname}?impersonate=agency&aid=${agency.id}&sid=${newSessionId}`;
        navigate(newUrl, { replace: true });
        toast.success(`Switched to ${agency.name}`);
      } else {
        toast.error('Failed to switch agency. Please try again.');
      }
    } catch (error) {
      console.error('Error switching agency:', error);
      toast.error('Failed to switch agency');
    } finally {
      setIsSwitching(false);
    }
  };

  const handleSwitchPatient = async (patient: Patient) => {
    if (!info || patient.id === info.targetId || isSwitching) return;

    setIsSwitching(true);
    try {
      // End current session
      await endImpersonation(info.sessionId);

      // Start new impersonation session
      const newSessionId = await startImpersonation({
        type: 'patient',
        patientId: patient.id,
        patientName: `${patient.first_name} ${patient.last_name}`,
      });

      if (newSessionId) {
        // Navigate to the triage page with new impersonation params
        const newUrl = `/triage?impersonate=patient&pid=${patient.id}&sid=${newSessionId}`;
        navigate(newUrl, { replace: true });
        toast.success(`Switched to ${patient.first_name} ${patient.last_name}`);
      } else {
        toast.error('Failed to switch patient. Please try again.');
      }
    } catch (error) {
      console.error('Error switching patient:', error);
      toast.error('Failed to switch patient');
    } finally {
      setIsSwitching(false);
    }
  };

  if (!info) return null;

  return (
    <div className="fixed top-0 left-0 right-0 z-50 bg-amber-500 text-amber-950 px-4 py-2 shadow-lg">
      <div className="max-w-7xl mx-auto flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Eye className="h-5 w-5" />
          <span className="font-medium">
            Viewing as {info.type === 'patient' ? 'Patient' : 'Agency Admin'}:
          </span>
          
          {/* Agency switcher dropdown */}
          {info.type === 'agency' && agencies.length > 1 ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="bg-amber-600/30 hover:bg-amber-600/50 text-amber-950 gap-2 px-3 py-1 h-auto"
                  disabled={isSwitching}
                >
                  {isSwitching ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Building2 className="h-4 w-4" />
                  )}
                  <span className="font-semibold">{info.targetName}</span>
                  <ChevronDown className="h-3 w-3" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-64 bg-background border border-border">
                <DropdownMenuLabel className="flex items-center gap-2">
                  <ArrowRightLeft className="h-4 w-4" />
                  Switch Agency
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                {agencies.map((agency) => (
                  <DropdownMenuItem
                    key={agency.id}
                    onClick={() => handleSwitchAgency(agency)}
                    className={agency.id === info.targetId ? 'bg-accent' : ''}
                    disabled={isSwitching}
                  >
                    <Building2 className="h-4 w-4 mr-2" />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{agency.name}</p>
                      <p className="text-xs text-muted-foreground">{agency.code}</p>
                    </div>
                    {agency.id === info.targetId && (
                      <span className="text-xs text-primary font-medium">Current</span>
                    )}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          ) : info.type === 'patient' && patients.length > 1 ? (
            /* Patient switcher dropdown */
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="bg-amber-600/30 hover:bg-amber-600/50 text-amber-950 gap-2 px-3 py-1 h-auto"
                  disabled={isSwitching}
                >
                  {isSwitching ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <User className="h-4 w-4" />
                  )}
                  <span className="font-semibold">{info.targetName}</span>
                  <ChevronDown className="h-3 w-3" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-64 bg-background border border-border max-h-80 overflow-y-auto">
                <DropdownMenuLabel className="flex items-center gap-2">
                  <ArrowRightLeft className="h-4 w-4" />
                  Switch Patient
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                {patients.map((patient) => (
                  <DropdownMenuItem
                    key={patient.id}
                    onClick={() => handleSwitchPatient(patient)}
                    className={patient.id === info.targetId ? 'bg-accent' : ''}
                    disabled={isSwitching}
                  >
                    <User className="h-4 w-4 mr-2" />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{patient.first_name} {patient.last_name}</p>
                      <p className="text-xs text-muted-foreground truncate">{patient.email}</p>
                    </div>
                    {patient.id === info.targetId && (
                      <span className="text-xs text-primary font-medium">Current</span>
                    )}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <div className="flex items-center gap-2 bg-amber-600/30 px-3 py-1 rounded-full">
              {info.type === 'patient' ? (
                <User className="h-4 w-4" />
              ) : (
                <Building2 className="h-4 w-4" />
              )}
              <span className="font-semibold">{info.targetName}</span>
            </div>
          )}
          
          <span className="text-amber-800 text-sm hidden md:inline">
            All actions are being logged for audit purposes
          </span>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={handleEndImpersonation}
          className="text-amber-950 hover:bg-amber-600/30"
          disabled={isSwitching}
        >
          <X className="h-4 w-4 mr-1" />
          End Session
        </Button>
      </div>
    </div>
  );
}
